import React from 'react';
import styled from "styled-components";
import { motion } from "framer-motion";
import ParticleBackground from "../components/ParticleBackground";

const ExperienceContainer = styled.div`
  min-height: 100vh;
  padding: 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${({ theme }) => theme.colors.background};
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 60px 15px;
  }
`;

const Title = styled.h2`
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 50px;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, #00d4aa);
    border-radius: 2px;
  }
`;

const ExperienceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  max-width: 1400px;
`;

const ExperienceCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(10, 25, 47, 0.9), rgba(20, 35, 60, 0.8));
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(100, 255, 218, 0.2);
  border: 1px solid rgba(100, 255, 218, 0.3);
  text-align: left;
  transition: all 0.3s ease;
  position: relative;
  width: 100%;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 60px rgba(100, 255, 218, 0.4);
    border-color: rgba(100, 255, 218, 0.6);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, #00d4aa);
    border-radius: 20px 20px 0 0;
  }

  @media (max-width: 768px) {
    padding: 30px 25px;
  }
`;

const ExperienceTitle = styled.h3`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 15px;
  font-weight: 600;
`;

const ExperienceDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
  opacity: 0.9;
`;

const CompanyName = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1.2rem;
  font-weight: 500;
  display: block;
  margin-bottom: 10px;
`;

const Duration = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.8;
`;

const experience = [
  {
    title: "Software Engineer Intern",
    company: "Beatior INC",
    duration: "2025 September - Present",
    description:
      "Contributing to full-stack product development using React, Node.js, and MongoDB. Collaborated with designers and backend teams in an Agile setup to ship customer-facing features on time.",

  },
  {
    title: "Research Intern",
    company: "IIT Bombay - Prof. Debabrata Maiti",
    duration: "2025 May - 2025 June",
    description:
      "Designed ML models (GNNs, transformers) to predict catalyst properties from SMILES strings with >85% accuracy. Automated large-scale data collection from PubChem and deployed a Flask web app for property prediction, enabling chemistry researchers to test molecules quickly.",
  },
  {
    title: "Machine Learning Intern",
    company: "NIT Warangal - Prof. Venkanna Udutalapally",
    duration: "2024 May - 2024 July",
    description:
      "Developed CNN-based classifiers to differentiate meat categories with >90% accuracy. Implemented advanced image augmentation techniques to generalize across datasets, demonstrating practical applications of computer vision in food quality analysis.",
  },
];

function Experience() {
  return (
    <ExperienceContainer>
      <ParticleBackground />
      <Title>Experience</Title>
      <ExperienceGrid>
        {experience.map((exp, index) => (
          <ExperienceCard
            key={index}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
          >
            <ExperienceTitle>{exp.title}</ExperienceTitle>
            <CompanyName>{exp.company}</CompanyName>
            <Duration>{exp.duration}</Duration>
            <ExperienceDescription>{exp.description}</ExperienceDescription>
          </ExperienceCard>
        ))}
      </ExperienceGrid>
    </ExperienceContainer>
  );
}

export default Experience;
